import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
// NOTE: remark-breaks converts single newlines to <br/> which can
// unintentionally break markdown lists and fenced code blocks coming
// from the LLM. We avoid using it to preserve original formatting.
import remarkWikiLink from 'remark-wiki-link';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight, oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { makeStyles, tokens } from '@fluentui/react-components';
import { Streamdown } from 'streamdown';
import { TextFormatter } from '../utils/textFormatter';
import { useTheme } from '../utils/theme';

// When receiving incremental/streamed markdown it's common to get
// partial fenced code blocks (an opening ``` without a closing ```).
// Many markdown parsers (including micromark used by react-markdown)
// expect balanced fences. This small helper scans the text for fenced
// code block openings and ensures any unclosed fence is closed by
// appending a matching fence at the end. This is intentionally
// conservative and only attempts to help with accidental partial
// output from streaming LLMs; it does not try to re-parse or mutate
// the fence contents.
function closeUnclosedFencedCodeBlocks(md: string): string {
  if (!md) return md;

  const lines = md.split(/\r?\n/);
  let inFence = false;
  let fenceMarker = '';

  for (const line of lines) {
    // Match exactly a fence start/stop like ``` or ~~~ optionally followed by a language
    const m = line.match(/^( {0,3})([`~]{3,})(.*)$/);
    if (m) {
      const marker = m[2];
      // If we're not inside a fence, this opens one.
      if (!inFence) {
        inFence = true;
        fenceMarker = marker;
      } else if (marker.startsWith(fenceMarker[0]) && marker.length >= fenceMarker.length) {
        // A matching (or longer) fence closes the current fence.
        inFence = false;
        fenceMarker = '';
      }
    }
  }

  if (inFence && fenceMarker) {
    // Append a newline and the closing fence so parsers see a complete block.
    return md + '\n\n' + fenceMarker + '\n';
  }

  return md;
}

const useStyles = makeStyles({
  messageContent: {
    fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
    '& p': {
      margin: `${tokens.spacingVerticalL} 0`,
      lineHeight: '1.8',
      color: 'var(--text-color, ' + tokens.colorNeutralForeground1 + ')',
    },
    '& p:first-child': {
      marginTop: 0,
    },
    '& p:last-child': {
      marginBottom: 0,
    },
    '& ul, & ol': {
      margin: `${tokens.spacingVerticalL} 0`,
      paddingLeft: tokens.spacingHorizontalXL,
      color: 'var(--text-color, ' + tokens.colorNeutralForeground1 + ')',
    },
    '& ul': {
      listStyleType: 'disc',
    },
    '& ol': {
      listStyleType: 'decimal',
    },
    '& li': {
      margin: `${tokens.spacingVerticalM} 0`,
      lineHeight: '1.75',
    },
    '& li > p': {
      margin: `${tokens.spacingVerticalM} 0`,
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: `${tokens.spacingVerticalL} 0 ${tokens.spacingVerticalS} 0`,
      fontWeight: tokens.fontWeightSemibold,
      lineHeight: '1.3',
      color: 'var(--text-color, ' + tokens.colorNeutralForeground1 + ')',
    },
    '& h1:first-child, & h2:first-child, & h3:first-child': {
      marginTop: 0,
    },
    '& h1': {
      fontSize: tokens.fontSizeBase500,
    },
    '& h2': {
      fontSize: tokens.fontSizeBase400,
    },
    '& h3': {
      fontSize: tokens.fontSizeBase300,
    },
    '& h4': {
      fontSize: tokens.fontSizeBase300,
    },
    '& h5': {
      fontSize: tokens.fontSizeBase200,
    },
    '& h6': {
      fontSize: tokens.fontSizeBase200,
    },
    '& blockquote': {
      margin: `${tokens.spacingVerticalM} 0`,
      borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
      fontStyle: 'italic',
      color: tokens.colorNeutralForeground2,
      backgroundColor: tokens.colorBrandBackground2,
      padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
      borderRadius: '0 6px 6px 0',
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      margin: `${tokens.spacingVerticalM} 0`,
      fontSize: tokens.fontSizeBase300,
    },
    '& th, & td': {
      padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      textAlign: 'left',
      lineHeight: '1.5',
    },
    '& th': {
      backgroundColor: tokens.colorBrandBackground2,
      fontWeight: 600,
      fontFamily: "'Syne', system-ui, sans-serif",
      fontSize: '11.5px',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: tokens.colorBrandForeground1,
    },
    '& td': {
      color: 'var(--text-color, ' + tokens.colorNeutralForeground1 + ')',
    },
    // Note: Code styling is now handled directly in the component to be theme-aware
    '& strong': {
      fontWeight: tokens.fontWeightSemibold,
      color: 'var(--text-color, ' + tokens.colorNeutralForeground1 + ')',
    },
    '& em': {
      fontStyle: 'italic',
      color: 'var(--text-color, ' + tokens.colorNeutralForeground1 + ')',
    },
    // Wiki link styles
    '& .wiki-link': {
      color: tokens.colorBrandForeground1,
      textDecoration: 'underline',
      fontWeight: tokens.fontWeightMedium,
    },
    '& .new-wiki-link': {
      color: tokens.colorPaletteRedForeground1,
      textDecoration: 'underline',
      fontWeight: tokens.fontWeightMedium,
    },
  },
});

interface MessageRendererProps {
  content: string;
  isUserMessage?: boolean;
}

export const MessageRenderer: React.FC<MessageRendererProps> = ({ content, isUserMessage = false }) => {
  const classes = useStyles();
  const { currentTheme } = useTheme();

  if (!isUserMessage) {
    return (
      <div className={classes.messageContent}>
        <Streamdown
          components={{
            a: ({ href, children, ...props }) => (
              <a
                {...props}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: tokens.colorBrandForeground1,
                  textDecoration: 'underline',
                  textDecorationColor: tokens.colorBrandStroke2,
                  textUnderlineOffset: '2px',
                }}
              >
                {children}
              </a>
            ),
          }}
        >
          {content}
        </Streamdown>
      </div>
    );
  }

  // Auto-format the content to add markdown-like formatting
  const formattedContent = TextFormatter.autoFormat(content);
  // Fix common streaming issue: an opening fenced code block without a closing
  // fence (partial stream). Ensure we close it so react-markdown/micromark can
  // render the partial content incrementally without throwing or rendering
  // broken nodes.
  const safeContent = closeUnclosedFencedCodeBlocks(formattedContent);

  // Override text color for user messages to ensure consistent rendering
  const messageStyle = isUserMessage ? {
    color: tokens.colorNeutralForeground1,
    '--text-color': tokens.colorNeutralForeground1
  } : {};

  return (
    <div className={classes.messageContent} style={messageStyle}>
      <ReactMarkdown
        remarkPlugins={[
          remarkGfm,
          [remarkWikiLink, {
            pageResolver: (name: string) => [name.replace(/ /g, '_').toLowerCase()],
            hrefTemplate: (permalink: string) => `#${permalink}`,
            wikiLinkClassName: 'wiki-link',
            newClassName: 'new-wiki-link',
            aliasDivider: '|'
          }]
        ]}
        components={{
          // Custom link rendering for security
          a: ({ href, children, ...props }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: tokens.colorBrandForeground1,
                textDecoration: 'underline',
                textDecorationColor: tokens.colorBrandStroke2,
                textUnderlineOffset: '2px',
              }}
              {...props}
            >
              {children}
            </a>
          ),
          // Enhanced table rendering
          table({ children, ...props }) {
            return (
              <div style={{ 
                overflowX: 'auto', 
                margin: `${tokens.spacingVerticalM} 0`,
                border: `1px solid ${tokens.colorNeutralStroke1}`,
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
              }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: tokens.fontSizeBase300,
                  }}
                  {...props}
                >
                  {children}
                </table>
              </div>
            );
          },
          // Enhanced code rendering: respect react-markdown's `inline` flag and
          // render fenced code blocks with syntax highlighting when a language
          // is present. If a block has no language, render a regular pre/code
          // block so it's displayed as expected instead of inline.
          code({ inline, className, children, ...props }: any) {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';

            // Block code (not inline)
            if (!inline) {
              if (language) {
                return (
                  <SyntaxHighlighter
                    style={currentTheme.isDark ? oneDark : oneLight}
                    language={language}
                    PreTag="div"
                    customStyle={{
                      margin: `${tokens.spacingVerticalS} 0`,
                      padding: tokens.spacingVerticalM,
                      backgroundColor: currentTheme.isDark ? '#2d2d2d' : '#f8f9fa',
                      borderRadius: tokens.borderRadiusMedium,
                      border: `1px solid ${tokens.colorNeutralStroke1}`,
                      fontSize: tokens.fontSizeBase200,
                    }}
                    {...props}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                );
              }

              // Block code without language
              const rawText = String(children).replace(/\n$/, '');
              const isSingleLine = !rawText.includes('\n') && rawText.length <= 80;

              // If it's a short single-line code snippet (like an ID), render it
              // inline-styled so it doesn't take the whole line. Otherwise render
              // as a full pre/code block to preserve multiline formatting.
              if (isSingleLine) {
                return (
                  <code
                    style={{
                      display: 'inline-block',
                      backgroundColor: currentTheme.isDark ? '#3f3f3f' : '#f3f2f1',
                      color: currentTheme.isDark ? '#ffffff' : '#323130',
                      padding: `2px ${tokens.spacingHorizontalXS}`,
                      borderRadius: tokens.borderRadiusSmall,
                      fontFamily: tokens.fontFamilyMonospace,
                      fontSize: tokens.fontSizeBase200,
                      border: `1px solid ${tokens.colorNeutralStroke2}`,
                      margin: '0 0.2em',
                    }}
                    {...props}
                  >
                    {rawText}
                  </code>
                );
              }

              // Multiline block: render as pre/code
              return (
                <pre
                  style={{
                    margin: `${tokens.spacingVerticalS} 0`,
                    padding: tokens.spacingVerticalM,
                    backgroundColor: currentTheme.isDark ? '#2d2d2d' : '#f8f9fa',
                    borderRadius: tokens.borderRadiusMedium,
                    border: `1px solid ${tokens.colorNeutralStroke1}`,
                    fontSize: tokens.fontSizeBase200,
                    overflow: 'auto',
                    color: currentTheme.isDark ? '#ffffff' : '#323130',
                  }}
                >
                  <code
                    style={{
                      fontFamily: tokens.fontFamilyMonospace,
                      whiteSpace: 'pre-wrap',
                      color: 'inherit',
                    }}
                    {...props}
                  >
                    {rawText}
                  </code>
                </pre>
              );
            }

            // Inline code
            return (
              <code
                className={className}
                style={{
                  backgroundColor: currentTheme.isDark ? '#3f3f3f' : '#f3f2f1',
                  color: currentTheme.isDark ? '#ffffff' : '#323130',
                  padding: `2px ${tokens.spacingHorizontalXS}`,
                  borderRadius: tokens.borderRadiusSmall,
                  fontFamily: tokens.fontFamilyMonospace,
                  fontSize: '0.9em',
                  border: `1px solid ${tokens.colorNeutralStroke2}`,
                }}
                {...props}
              >
                {children}
              </code>
            );
          },
        }}
      >
          {safeContent}
      </ReactMarkdown>
    </div>
  );
};

export default MessageRenderer;
